import { useEffect } from "react";
import { PushNotifications } from "@capacitor/push-notifications";
import { useChatTokenQuery } from "Api/Queries/Chat/useChatTokenQuery";
import { useChatPushRegistration } from "Hooks/Chat/useChatPushRegistration";
import { useStreamChatClient } from "Hooks/Chat/useStreamChatClient";
import { useLoggedInUser } from "Hooks/useLoggedInUser";
import { useNavigate } from "Navigation/useNavigate";
import { isAuthenticated } from "Utils/AuthUtils";
import { parseChatChannelIdFromPush } from "Utils/Chat/parseChatChannelIdFromPush";
import { appUrl } from "Utils/UrlUtils";

/**
 * App-wide chat push glue for the native shells (iOS and Android). Renders nothing. Mount ONLY
 * behind `isNativePlatform()` — the Capacitor push plugin is not implemented on plain web.
 *
 * - Keeps the device registered with Stream for the active persona, via the same shared chat
 *   client the unread badge uses (no extra socket, no extra token request).
 * - Routes a tapped notification to its channel. The plugin retains the launch notification
 *   until a listener consumes it, so a cold-start tap lands on the channel too.
 *
 * Foreground presentation is decided natively by each shell (iOS suppresses it with
 * `presentationOptions: []`), so there is deliberately no `pushNotificationReceived` handling here.
 */
export const ChatPushBridge = () => {
  const user = useLoggedInUser();
  const { data: chatToken } = useChatTokenQuery(
    isAuthenticated(user),
    user?.activeContextPublicID,
  );
  const { client } = useStreamChatClient(chatToken);
  const navigate = useNavigate();

  useChatPushRegistration(client);

  useEffect(() => {
    const handle = PushNotifications.addListener(
      "pushNotificationActionPerformed",
      ({ notification }) => {
        const channelId = parseChatChannelIdFromPush(notification.data);
        navigate(channelId ? `${appUrl("chat")}/${channelId}` : appUrl("chat"));
      },
    );

    return () => {
      void handle.then((h) => h.remove());
    };
  }, [navigate]);

  return null;
};
